import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation, Navigate, useNavigate } from 'react-router-dom';
import styles from './AdminLayout.module.css';
import { getToken, setToken } from '../../lib/api';
import LogoIcon from '../../icons/LogoIcon';
import HomeIcon from '../../icons/HomeIcon';
import UsersIcon from '../../icons/UsersIcon';
import ListIcon from '../../icons/ListIcon';
import ShoppingBasketIcon from '../../icons/ShoppingBasketIcon';
import CoinIcon from '../../icons/CoinIcon';
import BadgePercentIcon from '../../icons/BadgePercentIcon';
import MessagesIcon from '../../icons/MessagesIcon';
import MegaphoneIcon from '../../icons/MegaphoneIcon';
import { FiShare2, FiSend } from 'react-icons/fi';
import NotificationBell from '../NotificationBell/NotificationBell';
import ConfirmModal from '../ConfirmModal/ConfirmModal';

const NAV_ITEMS = [
    { to: '/dashboard', label: 'Dashboard', Icon: HomeIcon },
    { to: '/users', label: 'Users', Icon: UsersIcon },
    { to: '/listings', label: 'Listings', Icon: ListIcon },
    { to: '/orders', label: 'Orders', Icon: ShoppingBasketIcon },
    { to: '/payouts', label: 'Payouts', Icon: FiSend },
    { to: '/points', label: 'Points', Icon: CoinIcon },
    { to: '/coupon', label: 'Coupon', Icon: BadgePercentIcon },
    { to: '/promotions', label: 'Promotions', Icon: MegaphoneIcon },
    { to: '/affiliates', label: 'Affiliates', Icon: FiShare2 },
    { to: '/support', label: 'Support', Icon: MessagesIcon },
];

const AdminLayout = () => {
    const { pathname } = useLocation();
    const navigate = useNavigate();
    // Below lg the sidebar is an off-canvas drawer; from lg up it is always
    // shown and this state is inert.
    const [navOpen, setNavOpen] = useState(false);
    const [confirmLogout, setConfirmLogout] = useState(false);
    const currentItem = NAV_ITEMS.find((item) => pathname.startsWith(item.to));
    const pageTitle = currentItem ? currentItem.label : '';

    // Tapping a nav item navigates, so the drawer has to close itself — the
    // links are inside it and would otherwise stay covering the page.
    useEffect(() => {
        setNavOpen(false);
    }, [pathname]);

    // Escape closes the drawer, matching what the modals here already do.
    useEffect(() => {
        if (!navOpen) return undefined;
        const onKey = (event) => {
            if (event.key === 'Escape') setNavOpen(false);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [navOpen]);

    // Hooks must run before this bail-out, or the hook count changes between
    // the signed-in and signed-out renders.
    if (!getToken()) {
        return <Navigate to="/" replace />;
    }

    const handleLogout = () => {
        setConfirmLogout(false);
        setToken(null);
        navigate('/');
    };

    return (
        <div className="d-flex vh-100 overflow-hidden">
            {/* Backdrop only exists while the drawer is open, and only below lg
                where the drawer is a thing at all. */}
            {navOpen && (
                <button
                    type="button"
                    className={`${styles.backdrop} d-lg-none`}
                    aria-label="Close navigation"
                    onClick={() => setNavOpen(false)}
                />
            )}

            {/* From lg up this rail reserves the collapsed (icon-only) width, and
                the sidebar inside it expands over the page on hover/focus
                instead of pushing the content aside. */}
            <div className={`${styles.sidebarRail} flex-shrink-0`}>
                <aside
                    className={`${styles.sidebar} ${navOpen ? styles.sidebarOpen : ''} d-flex flex-column flex-shrink-0 p-3 overflow-y-auto`}
                >
                    <div className={`${styles.logo} px-2 px-lg-1 py-3 d-flex align-items-center justify-content-between`}>
                        {/* Collapsed desktop rail shows the square app icon; the
                            full wordmark shows on hover and in the mobile drawer. */}
                        <img
                            src={`${process.env.PUBLIC_URL}/agrobridge-mark.svg`}
                            alt=""
                            aria-hidden="true"
                            className={styles.logoIcon}
                        />
                        <span className={styles.logoFull}>
                            <LogoIcon size={37} />
                        </span>
                        <button
                            type="button"
                            className={`${styles.drawerClose} d-lg-none`}
                            aria-label="Close navigation"
                            onClick={() => setNavOpen(false)}
                        >
                            <span aria-hidden="true">&times;</span>
                        </button>
                    </div>

                    <nav className="d-flex flex-column gap-4 mt-5">
                        {NAV_ITEMS.map(({ to, label, Icon }) => (
                            <NavLink
                                key={to}
                                to={to}
                                className={({ isActive }) =>
                                    `${styles.navLink} ${isActive ? styles.navLinkActive : ''} d-flex align-items-center gap-2`
                                }
                            >
                                <Icon size={16} />
                                <span className={styles.navLabel}>{label}</span>
                            </NavLink>
                        ))}
                    </nav>
                </aside>
            </div>

            <div className="flex-grow-1 d-flex flex-column overflow-hidden">
                <header
                    className={`${styles.topbar} d-flex align-items-center justify-content-between gap-2 px-3 px-md-4 py-3`}
                >
                    <div className={`${styles.topbarLeft} d-flex align-items-center gap-2 gap-md-3`}>
                        <button
                            type="button"
                            className={`${styles.menuButton} d-lg-none`}
                            aria-label="Open navigation"
                            aria-expanded={navOpen}
                            onClick={() => setNavOpen(true)}
                        >
                            <span className={styles.menuBar} />
                            <span className={styles.menuBar} />
                            <span className={styles.menuBar} />
                        </button>
                        <h1 className={`${styles.pageTitle} mb-0 text-truncate`}>{pageTitle}</h1>
                    </div>

                    <div className="d-flex align-items-center gap-2 gap-md-3 flex-shrink-0">
                        <NotificationBell />
                        <button type="button" className={styles.avatar} onClick={() => setConfirmLogout(true)} title="Log out" style={{ border: 'none', cursor: 'pointer' }}>
                            MK
                        </button>
                    </div>
                </header>

                <main className="flex-grow-1 p-3 p-md-4 overflow-y-auto">
                    <Outlet />
                </main>
            </div>

            {confirmLogout && (
                <ConfirmModal
                    title="Log out"
                    message="Are you sure you want to log out?"
                    confirmLabel="Log out"
                    onConfirm={handleLogout}
                    onClose={() => setConfirmLogout(false)}
                />
            )}
        </div>
    );
};

export default AdminLayout;
